import { Button, Dialog, DialogContent, DialogFooter } from '@civilgrid/ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type RefObject, useRef, useState } from 'react';
import { activateExtractAutomation, deleteExtractAutomation, type ExtractAutomation } from '~/api/extract';
import { Notify } from '~/components2/Notify';
import { EXTRACT_ADMIN_STATE_QUERY_KEY } from './common';
import { EmptyRow, formatDate, Table, tdClass } from './ExtractTable';

const HEADERS = ['Name', 'Automation ID', 'Webhook URL', 'Created', 'Status', 'Actions'];

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  successMessage,
  errorMessage,
  onClose,
  successFocusRef,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => Promise<unknown>;
  successMessage: string;
  errorMessage: string;
  onClose: () => void;
  successFocusRef: RefObject<HTMLElement | null>;
}) {
  const queryClient = useQueryClient();
  const confirmedRef = useRef(false);
  const mutation = useMutation({
    mutationFn: onConfirm,
    onSuccess: () => {
      confirmedRef.current = true;
      Notify.info({ title, message: successMessage });
      queryClient.invalidateQueries({ queryKey: EXTRACT_ADMIN_STATE_QUERY_KEY });
      onClose();
    },
    onError: () => {
      Notify.error({ title, message: errorMessage });
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        role="alertdialog"
        title={title}
        description={message}
        data-testid="extract-automation-confirm-dialog"
        onCloseAutoFocus={(event) => {
          if (!confirmedRef.current) return;
          // The refetch removes or changes the opener's row, so focus goes to the section label instead.
          event.preventDefault();
          successFocusRef.current?.focus();
        }}
      >
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="extract-automation-confirm-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            data-testid="extract-automation-confirm"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ExtractAutomations({ automations }: { automations: ExtractAutomation[] }) {
  const [switchTarget, setSwitchTarget] = useState<ExtractAutomation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExtractAutomation | null>(null);
  const activeAutomation = automations.find((automation) => automation.status === 'active');
  const labelRef = useRef<HTMLDivElement>(null);

  return (
    <section className="w-full">
      <div
        ref={labelRef}
        tabIndex={-1}
        className="mb-1 rounded-sm text-sm font-medium focus-ring"
        data-testid="extract-automations-label"
      >
        Automations
      </div>
      <Table headers={HEADERS} testid="extract-state-automations" label="Automations">
        {automations.length ? (
          automations.map((automation) => (
            <tr key={automation.id} data-testid={`extract-automation-row-${automation.id}`}>
              <td className={tdClass}>{automation.name}</td>
              <td className={tdClass}>
                <code>{automation.automationId}</code>
              </td>
              <td className={`${tdClass} break-all`}>{automation.webhookUrl}</td>
              <td className={tdClass}>{formatDate(automation.createdAt)}</td>
              <td className={tdClass}>{automation.status === 'active' ? 'Active' : 'Archived'}</td>
              <td className={tdClass}>
                {automation.status !== 'active' && (
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="small"
                      aria-haspopup="dialog"
                      aria-label={`Make active: ${automation.name}`}
                      onClick={() => setSwitchTarget(automation)}
                      data-testid={`extract-automation-activate-${automation.id}`}
                    >
                      Make active
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      aria-haspopup="dialog"
                      aria-label={`Delete: ${automation.name}`}
                      onClick={() => setDeleteTarget(automation)}
                      data-testid={`extract-automation-delete-${automation.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))
        ) : (
          <EmptyRow colSpan={HEADERS.length} message="No automations." />
        )}
      </Table>
      {switchTarget && (
        <ConfirmDialog
          title="Switch active automation"
          message={
            activeAutomation
              ? `${activeAutomation.name} will be archived and ${switchTarget.name} becomes active.`
              : `No automation is active. ${switchTarget.name} becomes active.`
          }
          confirmLabel="Make active"
          onConfirm={() => activateExtractAutomation(switchTarget.id)}
          successMessage={`Automation "${switchTarget.name}" is now active.`}
          errorMessage="An error occurred while switching the automation. Check logs for details."
          onClose={() => setSwitchTarget(null)}
          successFocusRef={labelRef}
        />
      )}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete automation"
          message={`${deleteTarget.name} will be permanently deleted. This can't be undone.`}
          confirmLabel="Delete"
          onConfirm={() => deleteExtractAutomation(deleteTarget.id)}
          successMessage={`Automation "${deleteTarget.name}" was deleted.`}
          errorMessage="An error occurred while deleting the automation. Check logs for details."
          onClose={() => setDeleteTarget(null)}
          successFocusRef={labelRef}
        />
      )}
    </section>
  );
}
